import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, throwIfError } from "../client";
import {
  fleetEqualizationKeys,
  fleetReportKeys,
  platformWeighingKeys,
  scheduleKeys,
} from "../query-keys";
import type { BoatId, PlatformWeighingId } from "@/types/identifiers";
import type { components } from "@/api/generated/schema";

export { platformWeighingKeys } from "../query-keys";

type PlatformWeighingCreate = components["schemas"]["PlatformWeighingCreate"];
type PlatformWeighingUpdate = components["schemas"]["PlatformWeighingUpdate"];

export function usePlatformWeighings(boatId: BoatId) {
  return useQuery({
    queryKey: platformWeighingKeys.byBoat(boatId),
    queryFn: async () => {
      const result = await api.GET(
        "/api/boats/{boat_id}/platform-weighings",
        { params: { path: { boat_id: boatId } } },
      );
      return throwIfError(result, "Failed to fetch platform weighings");
    },
    enabled: !!boatId,
  });
}

function invalidateAll(qc: ReturnType<typeof useQueryClient>, boatId: BoatId) {
  qc.invalidateQueries({ queryKey: platformWeighingKeys.byBoat(boatId) });
  // Any fleet equalization/report view that references this boat must recompute.
  qc.invalidateQueries({ queryKey: fleetEqualizationKeys.all });
  qc.invalidateQueries({ queryKey: fleetReportKeys.all });
  // A signed-off weighing flips a WEIGH_IN schedule slot to Done.
  qc.invalidateQueries({ queryKey: scheduleKeys.all });
}

export function useCreatePlatformWeighing(boatId: BoatId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: PlatformWeighingCreate) => {
      const result = await api.POST(
        "/api/boats/{boat_id}/platform-weighings",
        { params: { path: { boat_id: boatId } }, body: data },
      );
      return throwIfError(result, "Failed to create platform weighing");
    },
    onSuccess: () => invalidateAll(qc, boatId),
  });
}

export function useUpdatePlatformWeighing(boatId: BoatId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      weighingId,
      data,
    }: {
      weighingId: PlatformWeighingId;
      data: PlatformWeighingUpdate;
    }) => {
      const result = await api.PATCH(
        "/api/boats/{boat_id}/platform-weighings/{weighing_id}",
        {
          params: { path: { boat_id: boatId, weighing_id: weighingId } },
          body: data,
        },
      );
      return throwIfError(result, "Failed to update platform weighing");
    },
    onSuccess: () => invalidateAll(qc, boatId),
  });
}

export function useSignOffPlatformWeighing(boatId: BoatId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      weighingId,
      overrideGate = false,
    }: {
      weighingId: PlatformWeighingId;
      overrideGate?: boolean;
    }) => {
      const result = await api.POST(
        "/api/boats/{boat_id}/platform-weighings/{weighing_id}/sign-off",
        {
          params: { path: { boat_id: boatId, weighing_id: weighingId } },
          body: { override_gate: overrideGate },
        },
      );
      return throwIfError(result, "Failed to sign off platform weighing");
    },
    onSuccess: () => invalidateAll(qc, boatId),
  });
}

export function useUnlockPlatformWeighing(boatId: BoatId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (weighingId: PlatformWeighingId) => {
      const result = await api.POST(
        "/api/boats/{boat_id}/platform-weighings/{weighing_id}/unlock",
        {
          params: { path: { boat_id: boatId, weighing_id: weighingId } },
        },
      );
      return throwIfError(result, "Failed to unlock platform weighing");
    },
    onSuccess: () => invalidateAll(qc, boatId),
  });
}

export function useDeletePlatformWeighing(boatId: BoatId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (weighingId: PlatformWeighingId) => {
      const result = await api.DELETE(
        "/api/boats/{boat_id}/platform-weighings/{weighing_id}",
        {
          params: { path: { boat_id: boatId, weighing_id: weighingId } },
        },
      );
      return throwIfError(result, "Failed to delete platform weighing");
    },
    onSuccess: () => invalidateAll(qc, boatId),
  });
}
